import { FC, useEffect, useRef, useState } from 'react';
import { useAccountCreationContext } from '../../common/context/AccountCreationContext';

// const CENTER_IMAGE = 'https://cdn.laredoute.com/cdn-cgi/image/width=1200,height=1200,fit=pad,dpr=1/products/8/3/f/83fbca5d4e55201077694bedda697e3e.jpg';

type OrbitMotion = {
  angle: number;
  speed: number;
  radiusX: number;
  radiusY: number;
  targetRadiusX: number;
  targetRadiusY: number;
};

interface AnalysisAnimationProps {
  centerImageUrl: string;
}

const AnalysisAnimation: FC<AnalysisAnimationProps> = ({ centerImageUrl }) => {
  const { selectedProducts } = useAccountCreationContext();

  const productsToShow = selectedProducts.filter(
    (p) => p?.product_images?.length > 0 && p?.product_images[0] !== centerImageUrl
  );

  const [orbits, setOrbits] = useState<OrbitMotion[]>(
    productsToShow.map(() => {
      const initRadiusX = 20 + Math.random() * 25;
      const initRadiusY = 15 + Math.random() * 20;
      return {
        angle: Math.random() * Math.PI * 2,
        speed: 0.005 + Math.random() * 0.01,
        radiusX: initRadiusX,
        radiusY: initRadiusY,
        targetRadiusX: initRadiusX,
        targetRadiusY: initRadiusY,
      };
    })
  );

  useEffect(() => {
    let frameId: number;

    const animate = () => {
      setOrbits((prev) =>
        prev.map((orbit) => {
          let { angle, speed, radiusX, radiusY, targetRadiusX, targetRadiusY } = orbit;

          // Sometimes flip direction
          if (Math.random() < 0.001) {
            speed *= -1;
          }

          // Sometimes choose a new target orbit size
          if (Math.random() < 0.002) {
            targetRadiusX = 20 + Math.random() * 25;
            targetRadiusY = 15 + Math.random() * 20;
          }

          // Smoothly move radii toward target
          radiusX += (targetRadiusX - radiusX) * 0.02;
          radiusY += (targetRadiusY - radiusY) * 0.02;

          // Advance orbit angle
          angle += speed;

          return { angle, speed, radiusX, radiusY, targetRadiusX, targetRadiusY };
        })
      );

      frameId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(frameId);
  }, []);

  return (
    <div
      className="pop-relative pop-w-full"
      style={{
        height: '50vh',
        padding: '1rem',
        overflow: 'hidden',
      }}
    >
      {/* Center image */}
      <img
        src={centerImageUrl}
        alt="center"
        className="pop-absolute"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          objectFit: 'contain',
          borderRadius: '1rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10,
          maxHeight: '100%',
          maxWidth: '50%',
        }}
      />

      {/* Orbiting products */}
      {productsToShow.map((product, i) => {
        const orbit = orbits[i];
        const x = orbit.radiusX * Math.cos(orbit.angle);
        const y = orbit.radiusY * Math.sin(orbit.angle);

        return (
          <img
            key={i}
            src={product.product_images[0]}
            alt={product.product_label}
            className="pop-absolute"
            style={{
              top: `calc(50% + ${y}%)`,
              left: `calc(50% + ${x}%)`,
              transform: 'translate(-50%, -50%)',
              objectFit: 'cover',
              borderRadius: '0.75rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              zIndex: 5,
              maxHeight: '25%',
              maxWidth: '40%',
            }}
          />
        );
      })}
    </div>
  );
};

export default AnalysisAnimation;
